'use client';
import React, { useEffect, useRef } from 'react';

const packages = [
  {
    name: 'Essential',
    price: '$995',
    features: [
      'Executive Résumé (1 version)',
      'Cover Letter',
      'Basic LinkedIn Review',
      '1 Revision Round',
      'Performance Guarantee',
      '5–7 Business Day Turnaround',
    ],
    popular: false,
    cta: 'Get Started',
  },
  {
    name: 'Professional',
    price: '$1,895',
    features: [
      'Executive Résumé (2 versions)',
      'Cover Letter',
      'Full LinkedIn Profile Rewrite',
      '2 Revision Rounds',
      'Email Support (30 days)',
      'Performance Guarantee',
    ],
    popular: true,
    cta: 'Get Started',
  },
  {
    name: 'Executive Elite',
    price: '$3,495',
    features: [
      'Executive Résumé (3 versions)',
      'Cover Letter + Executive Bio',
      'Full LinkedIn Optimisation',
      'Salary Negotiation Coaching',
      'Unlimited Revisions (30 days)',
      'Priority 3-Day Turnaround',
      'Performance Guarantee',
    ],
    popular: false,
    cta: 'Get Started',
  },
];

export default function PackagesSection() {
  const headRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    if (headRef?.current) observer?.observe(headRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="section-plum relative py-24 lg:py-32 overflow-hidden" id="packages">
      <div className="noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div ref={headRef} className="reveal-section text-center mb-16">
          <span className="section-index block mb-4" style={{ color: 'rgba(201,168,76,0.5)' }}>
            INVESTMENT OPTIONS
          </span>
          <h2
            className="font-fraunces font-medium mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', color: '#F5F0E8', lineHeight: '1.05' }}
          >
            Choose Your Package
          </h2>
          <p className="max-w-xl mx-auto text-base font-light" style={{ color: 'rgba(245,240,232,0.6)' }}>
            Every package is personally delivered. Performance guaranteed. Priced to reflect the value of the results.
          </p>
        </div>

        {/* Package cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {packages?.map((pkg) => (
            <div
              key={pkg?.name}
              className="relative rounded-sm p-8 flex flex-col transition-all duration-300"
              style={{
                background: pkg?.popular ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.04)',
                border: pkg?.popular ? '1px solid rgba(201,168,76,0.5)' : '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {pkg?.popular && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-sm text-xs font-mono-custom font-medium tracking-widest"
                  style={{ background: '#C9A84C', color: '#4A1942' }}
                >
                  MOST POPULAR
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-fraunces font-medium text-xl mb-2" style={{ color: '#F5F0E8' }}>{pkg?.name}</h3>
                <div className="proof-number" style={{ fontSize: 'clamp(2.5rem, 4vw, 3.5rem)' }}>{pkg?.price}</div>
              </div>

              <div className="gold-rule-full opacity-10 mb-6" />

              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {pkg?.features?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm" style={{ color: 'rgba(245,240,232,0.75)' }}>
                    <svg className="flex-shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <a
                href="/contact"
                className={`w-full text-center py-3.5 rounded-sm text-sm font-semibold tracking-wide transition-all duration-200 ${pkg?.popular ? 'cta-gold' : 'border'}`}
                style={!pkg?.popular ? { borderColor: 'rgba(201,168,76,0.3)', color: '#C9A84C' } : {}}
              >
                {pkg?.cta}
              </a>
            </div>
          ))}
        </div>

        <p className="text-center mt-8 text-sm" style={{ color: 'rgba(245,240,232,0.4)' }}>
          Not sure which package is right for you?{' '}
          <a href="/contact" className="cta-secondary text-sm">Book a free consultation.</a>
        </p>

        {/* Add-on services */}
        <div className="mt-16">
          <h3
            className="font-fraunces font-medium text-center mb-2"
            style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', color: '#F5F0E8' }}
          >
            Focused Add-On Services
          </h3>
          <p className="text-center text-sm mb-10" style={{ color: 'rgba(245,240,232,0.5)' }}>
            Targeted solutions you can add to any package or book as standalone engagements.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* SWOT Analysis */}
            <div
              className="rounded-sm p-8 flex flex-col"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <span className="text-xs font-mono-custom tracking-widest uppercase mb-2 block" style={{ color: 'rgba(201,168,76,0.6)' }}>
                    Strategic Clarity
                  </span>
                  <h4 className="font-fraunces font-medium text-xl" style={{ color: '#F5F0E8' }}>SWOT Analysis</h4>
                </div>
                <div className="proof-number text-right flex-shrink-0" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>$350</div>
              </div>
              <div className="gold-rule-full opacity-10 mb-5" />
              <p className="text-sm leading-relaxed mb-6 flex-1" style={{ color: 'rgba(245,240,232,0.65)' }}>
                A comprehensive personal SWOT analysis of your Strengths, Weaknesses, Opportunities, and Threats. Gain a clear, strategic picture of where you stand and how to position yourself powerfully for your next role.
              </p>
              <ul className="flex flex-col gap-2 mb-8">
                {['In-depth personal assessment', 'Competitive positioning strategy', 'Written report + coaching debrief', 'Actionable next steps']?.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm" style={{ color: 'rgba(245,240,232,0.75)' }}>
                    <svg className="flex-shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="/contact"
                className="w-full text-center py-3.5 rounded-sm text-sm font-semibold tracking-wide transition-all duration-200 border"
                style={{ borderColor: 'rgba(201,168,76,0.3)', color: '#C9A84C' }}
              >
                Get Started
              </a>
            </div>

            {/* 30-60-90 Day Action Plan */}
            <div
              className="rounded-sm p-8 flex flex-col"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <span className="text-xs font-mono-custom tracking-widest uppercase mb-2 block" style={{ color: 'rgba(201,168,76,0.6)' }}>
                    Career Roadmap
                  </span>
                  <h4 className="font-fraunces font-medium text-xl" style={{ color: '#F5F0E8' }}>30-60-90 Day Action Plan</h4>
                </div>
                <div className="proof-number text-right flex-shrink-0" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>$425</div>
              </div>
              <div className="gold-rule-full opacity-10 mb-5" />
              <p className="text-sm leading-relaxed mb-6 flex-1" style={{ color: 'rgba(245,240,232,0.65)' }}>
                A structured, milestone-driven career action plan covering your first 30, 60, and 90 days in a new role or job search. Designed to accelerate your transition and set you up for measurable early wins.
              </p>
              <ul className="flex flex-col gap-2 mb-8">
                {['Milestone-based career roadmap', 'Weekly priority actions', 'Progress tracking framework', 'Personalised coaching session']?.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm" style={{ color: 'rgba(245,240,232,0.75)' }}>
                    <svg className="flex-shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="/contact"
                className="w-full text-center py-3.5 rounded-sm text-sm font-semibold tracking-wide transition-all duration-200 border"
                style={{ borderColor: 'rgba(201,168,76,0.3)', color: '#C9A84C' }}
              >
                Get Started
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
